import { API_URL } from '../config/apiConfig';

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
        throw new Error('No authentication token found');
    }
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
};

export const getAllUsers = async (page = 1, limit = 25, sortField = 'username', sortDirection = 'asc') => {
    try {
        // Always use reporting view for Users page to see all users
        const url = `${API_URL}/api/users?view=reporting&page=${page}&limit=${limit}&sortField=${sortField}&sortDirection=${sortDirection}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch users');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getAllUsers:', error);
        throw error;
    }
};

export const getAllUsersNoPagination = async () => {
    try {
        const url = `${API_URL}/api/users?view=reporting&noPagination=true`;
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch users');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getAllUsers:', error);
        throw error;
    }
};

export const getUser = async (id) => {
    try {
        const response = await fetch(`${API_URL}/api/users/${id}/with-password`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch user');
        }
        
        const data = await response.json();
        return {
            ...data,
            password: data.password || '(Set new password to enable visibility)' // Ensure password is never undefined
        };
    } catch (error) {
        console.error('Error in getUser:', error);
        throw error;
    }
};

export const getCurrentUser = async () => {
    try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch current user');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getCurrentUser:', error);
        throw error;
    }
};

export const createUser = async (userData) => {
    try {
        console.log('Creating user with data:', JSON.stringify(userData, null, 2));
        
        const response = await fetch(`${API_URL}/api/users`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(userData),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            console.error('Server error response:', errorData);
            throw new Error(errorData?.error || 'Failed to create user');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in createUser:', error);
        throw error;
    }
};

export const updateUser = async (id, userData) => {
    try {
        const response = await fetch(`${API_URL}/api/users/${id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(userData),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to update user');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in updateUser:', error);
        throw error;
    }
};

export const updateUserPassword = async (id, newPassword) => {
    try {
        const response = await fetch(`${API_URL}/api/users/${id}/password`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ newPassword }),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to update password');
        }
        
        const data = await response.json();
        return {
            message: data.message,
            password: newPassword // Always use the new password for UI display
        };
    } catch (error) {
        console.error('Error in updateUserPassword:', error);
        throw error;
    }
};

export const updateProfile = async (profileData) => {
    try {
        const response = await fetch(`${API_URL}/api/users/profile/me`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(profileData),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to update profile');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in updateProfile:', error);
        throw error;
    }
};

export const deleteUser = async (id) => {
    try {
        const response = await fetch(`${API_URL}/api/users/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to delete user');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in deleteUser:', error);
        throw error;
    }
};

export const toggleUserStatus = async (id, isActive) => {
    try {
        const response = await fetch(`${API_URL}/api/users/${id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ isActive }),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to toggle user status');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in toggleUserStatus:', error);
        throw error;
    }
};

export const updateUserContact = async (id, contactData) => {
    try {
        const response = await fetch(`${API_URL}/api/users/${id}/contact`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(contactData),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to update user contact information');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in updateUserContact:', error);
        throw error;
    }
};

export const getUserProjectHours = async (userId, year = new Date().getFullYear()) => {
    try {
        const response = await fetch(`${API_URL}/api/users/${userId}/project-hours?year=${year}`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch project hours');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getUserProjectHours:', error);
        throw error;
    }
};

export const getUserPtoHours = async (userId, year = new Date().getFullYear(), isReporting = false) => {
    try {
        const viewParam = isReporting ? '&view=reporting' : '';
        const response = await fetch(`${API_URL}/api/users/${userId}/pto-hours?year=${year}${viewParam}`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch PTO hours');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getUserPtoHours:', error);
        throw error;
    }
};

export const getAllUsersPtoHours = async (year = new Date().getFullYear()) => {
    try {
        // First get all active users
        const users = await getAllUsersNoPagination();
        // Filter out admin users but include accountant users
        const activeUsers = Array.isArray(users) ? users.filter(user => user.isActive && user.role !== 'admin') : [];
        
        if (activeUsers.length === 0) {
            return [];
        }
        
        // Fetch PTO hours for each active user
        const ptoPromises = activeUsers.map(async (user) => {
            try {
                const ptoHours = await getUserPtoHours(user._id, year, true); // Pass isReporting=true
                return {
                    _id: user._id,
                    username: user.username,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    yearStart: ptoHours.yearStart,
                    ytdAccumulated: ptoHours.ytdAccumulated,
                    ytdUsed: ptoHours.ytdUsed,
                    available: ptoHours.available,
                    year
                };
            } catch (error) {
                console.error(`Error fetching PTO hours for user ${user.username}:`, error);
                // Return user with default values if there's an error
                return {
                    _id: user._id,
                    username: user.username,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    yearStart: user.yearStart || 0,
                    ytdAccumulated: user.ytdAccumulated || 0,
                    ytdUsed: 0,
                    available: (parseFloat(user.yearStart || 0) + parseFloat(user.ytdAccumulated || 0)),
                    year
                };
            }
        });
        
        const ptoData = await Promise.all(ptoPromises);
        
        // Sort by username
        return ptoData.sort((a, b) => a.username.localeCompare(b.username));
    } catch (error) {
        console.error('Error in getAllUsersPtoHours:', error);
        throw error;
    }
};

// 2FA related functions

export const getUserTwoFactorStatus = async (userId) => {
    try {
        const response = await fetch(`${API_URL}/api/users/${userId}/2fa`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch 2FA status');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getUserTwoFactorStatus:', error);
        throw error;
    }
};

export const updateForcePasswordChange = async (userId, enabled) => {
    try {
        const response = await fetch(`${API_URL}/api/users/${userId}/force-password-change`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ enabled }),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to update password change requirement');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in updateForcePasswordChange:', error);
        throw error;
    }
};

// testResetUserPasswordChange function removed as it's no longer needed

export const getOwnTwoFactorStatus = async () => {
    try {
        const response = await fetch(`${API_URL}/api/users/profile/me/2fa`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch 2FA status');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getOwnTwoFactorStatus:', error);
        throw error;
    }
};

export const updateUserTwoFactorStatus = async (userId, enabled) => {
    try {
        const response = await fetch(`${API_URL}/api/users/${userId}/2fa`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ enabled }),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to update 2FA status');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in updateUserTwoFactorStatus:', error);
        throw error;
    }
};

export const updateOwnTwoFactorStatus = async (enabled, currentPassword) => {
    try {
        const response = await fetch(`${API_URL}/api/users/profile/me/2fa`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ enabled, currentPassword }),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to update 2FA status');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in updateOwnTwoFactorStatus:', error);
        throw error;
    }
};

// Get active hourly users
export const getHourlyUsers = async () => {
    try {
        const response = await fetch(`${API_URL}/api/users/hourly`, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch hourly users');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in getHourlyUsers:', error);
        throw error;
    }
};

// Assign Administrative project to users temporarily
export const assignTemporaryAdminProject = async (userIds) => {
    try {
        const response = await fetch(`${API_URL}/api/projects/admin/assign-temporary`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ userIds }),
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to assign Administrative project');
        }
        
        return response.json();
    } catch (error) {
        console.error('Error in assignTemporaryAdminProject:', error);
        throw error;
    }
};
